// ADR 15.10.26d §1 — the device every field report attaches.
//
// Pure over `DeviceInputs`, so its suite runs under `node`; `readDeviceInputs`
// is the one reader of globals and is guarded so it never throws there. It
// imports `pwa/installAffordance.ts` and `pwa/displayMode.ts`, both pure: a
// deliberate exception to `installStrip.ts`'s "restate, don't import from
// `pwa/`", because restating a UA parser would fork it. It never imports
// `pwa/installPrompt.ts` — that module imports `installReports.ts`, so whether
// a prompt is held is an *input* here, not a read.
//
// §1a — `userAgentData`'s high-entropy hints are read at report time only and
// decide nothing (roadmap: no `userAgentData` for detection). Android's reduced
// UA hides the model, and the model is what makes a report actionable.

import i18next from 'i18next'

import { displayModeOf, type DisplayMode } from '../../pwa/displayMode'
import {
  EXCLUDED_DERIVATIVES, isInAppBrowser, majorOf, OEM_TOKENS, platformOf, type Platform,
} from '../../pwa/installAffordance'

export interface DeviceInputs {
  ua: string
  maxTouchPoints: number
  displayMode: DisplayMode
  promptCaptured: boolean
  locale: string
}

/** The globals, read once; under `node` (or a throwing getter) the neutral answers. */
export function readDeviceInputs(promptCaptured: boolean): DeviceInputs {
  const inputs: DeviceInputs = { ua: '', maxTouchPoints: 0, displayMode: 'browser', promptCaptured, locale: 'en' }
  try {
    const nav = typeof navigator === 'undefined' ? undefined : navigator
    inputs.ua = nav?.userAgent ?? ''
    inputs.maxTouchPoints = nav?.maxTouchPoints ?? 0
    if (typeof window !== 'undefined') inputs.displayMode = displayModeOf(window)
    inputs.locale = i18next.language ?? 'en'
  } catch {
    // Whatever was read stands; the rest keeps its neutral default.
  }
  return inputs
}

// A type alias rather than an interface: it travels as a Sentry context, which
// is a `Record<string, unknown>`, and only an alias is assignable to one.
export type DeviceRecord = {
  ua: string
  platform: Platform
  oemToken: string | null
  oemVersion: string | null
  chromiumMajor: number | null
  osName: 'android' | 'harmonyos' | 'ios' | 'other'
  osVersion: string | null
  displayMode: DisplayMode
  promptCaptured: boolean
  locale: string
  hints?: { model?: string; platformVersion?: string; fullVersionList?: string }
}

function oemTokenOf(ua: string): string | null {
  return OEM_TOKENS.find(([token]) => ua.includes(`${token}/`))?.[0] ?? null
}

function versionAfter(ua: string, token: string): string | null {
  return new RegExp(`${token}/(\\d[\\d.]*)`).exec(ua)?.[1] ?? null
}

function osOf(ua: string): Pick<DeviceRecord, 'osName' | 'osVersion'> {
  if (/iPhone|iPad|iPod/.test(ua)) {
    const ios = /OS (\d+)_(\d+)/.exec(ua)
    return { osName: 'ios', osVersion: ios === null ? null : `${ios[1]}.${ios[2]}` }
  }
  if (/OpenHarmony|HarmonyOS/.test(ua)) {
    const version = /OpenHarmony (\d[\d.]*)/.exec(ua) ?? /Android (\d[\d.]*)/.exec(ua)
    return { osName: 'harmonyos', osVersion: version?.[1] ?? null }
  }
  if (ua.includes('Android')) return { osName: 'android', osVersion: /Android (\d[\d.]*)/.exec(ua)?.[1] ?? null }
  return { osName: 'other', osVersion: null }
}

export function deviceRecord(inputs: DeviceInputs): DeviceRecord {
  const { ua } = inputs
  const oemToken = oemTokenOf(ua)
  const chromiumMajor = majorOf(ua, 'Chrome')
  return {
    ua,
    platform: platformOf(ua, inputs.maxTouchPoints),
    oemToken,
    oemVersion: oemToken === null ? null : versionAfter(ua, oemToken),
    chromiumMajor: chromiumMajor === 0 ? null : chromiumMajor,
    ...osOf(ua),
    displayMode: inputs.displayMode,
    promptCaptured: inputs.promptCaptured,
    locale: inputs.locale,
  }
}

/** The three tags Sentry searches on. */
export function deviceTags(r: DeviceRecord): Record<string, string> {
  return {
    'install.platform': r.platform,
    'browser.oem': r.oemToken ?? 'none',
    'browser.major': String(r.chromiumMajor ?? 0),
  }
}

/** Stock Chrome's own product tokens; anything else outside the parentheses is a browser's mark. */
const STOCK_TOKENS = new Set(['Mozilla', 'AppleWebKit', 'Chrome', 'Mobile', 'Safari', 'Version'])

/**
 * §3 — the product token of an Android Chromium browser we have not met, or
 * null. Android, not a WebView, not an in-app browser, carries `Chrome/`,
 * matches neither `OEM_TOKENS` nor `EXCLUDED_DERIVATIVES`, and carries a token
 * stock Chrome does not. Parenthesised comments (`Build/…`) are not tokens.
 */
export function unknownProductToken(ua: string): string | null {
  if (!ua.includes('Android') || ua.includes('; wv)') || isInAppBrowser(ua)) return null
  if (!ua.includes('Chrome/') || oemTokenOf(ua) !== null || EXCLUDED_DERIVATIVES.test(ua)) return null
  const outside = ua.replace(/\([^)]*\)/g, ' ')
  for (const match of outside.matchAll(/([A-Za-z][\w.-]*)\/\d/g)) {
    if (!STOCK_TOKENS.has(match[1])) return match[1]
  }
  return null
}

/** The maker's token, else an unknown one, else null — what a fingerprint and a latch key name. */
export function browserToken(r: DeviceRecord): string | null {
  return r.oemToken ?? unknownProductToken(r.ua)
}

/**
 * The token's own major when there is a token (the number 15.10.26c floors
 * on — HeyTap's `Chrome/` is reportedly frozen at 70), else Chromium's.
 */
function versionMajor(r: DeviceRecord): number {
  const token = browserToken(r)
  return token === null ? (r.chromiumMajor ?? 0) : majorOf(r.ua, token)
}

/** A synthetic report: one browser version on one platform is one issue. */
export function syntheticFingerprint(kind: string, r: DeviceRecord): string[] {
  return [kind, r.platform, browserToken(r) ?? 'none', String(versionMajor(r))]
}

/** A real error: Sentry still splits two different bugs on one browser by their stacks. */
export function caughtFingerprint(kind: string, r: DeviceRecord): string[] {
  return [kind, r.platform, browserToken(r) ?? 'none', '{{ default }}']
}

interface HighEntropyNavigator {
  userAgentData?: { getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>> }
}

export const HINTS_TIMEOUT_MS = 500

function listOf(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined
  return value.map((entry: { brand?: unknown; version?: unknown }) => `${String(entry.brand)} ${String(entry.version)}`).join('; ')
}

const text = (value: unknown) => (typeof value === 'string' ? value : undefined)

/** §1a — the hints, best-effort and bounded: absent, throwing, rejecting or slow returns `r` unchanged. */
export async function withHints(r: DeviceRecord, nav: object | undefined = globalThis.navigator): Promise<DeviceRecord> {
  const data = (nav as HighEntropyNavigator | undefined)?.userAgentData
  if (typeof data?.getHighEntropyValues !== 'function') return r
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const timeout = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), HINTS_TIMEOUT_MS) })
    const values = await Promise.race([data.getHighEntropyValues(['model', 'platformVersion', 'fullVersionList']), timeout])
    if (values === null) return r
    return {
      ...r,
      hints: { model: text(values.model), platformVersion: text(values.platformVersion), fullVersionList: listOf(values.fullVersionList) },
    }
  } catch {
    return r
  } finally {
    clearTimeout(timer)
  }
}
